/**
 * Rutas de hr — personas: empleados, nóminas, tiempo, ciclo de vida y portal del empleado.
 *
 * Se componen en `src/pages/index.jsx`. Es un fragmento, no un array: React Router
 * recorre los `<React.Fragment>` hijos de `<Routes>`, así que el JSX se mudó tal cual
 * —sin `key` ni comas— y el diff es el movimiento, nada más.
 *
 * `routeTable.test.jsx` congela las 174 rutas de la app contra la tabla previa a la
 * mudanza: perder una ruta aquí no da error, da un 404.
 */
import { Route, Navigate } from 'react-router-dom';
import { lazyWithRetry } from '@/lib/lazyWithRetry';
import { PermissionGuard } from '@/components/auth/PermissionGuard';
import { RedirectKeepSearch } from '@/pages/routeHelpers';

const RRHHEmpleados = lazyWithRetry(() => import("@/pages/RRHHEmpleados"));
const Nominas = lazyWithRetry(() => import("@/pages/Nominas"));
const PayrollDetail = lazyWithRetry(() => import("@/pages/PayrollDetail"));
const EmployeeDetail = lazyWithRetry(() => import("@/pages/EmployeeDetail"));
const PortalEmpleado = lazyWithRetry(() => import("@/pages/PortalEmpleado"));
const Onboarding = lazyWithRetry(() => import("@/pages/Onboarding"));
const InformesRRHH = lazyWithRetry(() => import("@/pages/InformesRRHH"));
const TimesheetManager = lazyWithRetry(() => import("@/pages/TimesheetManager"));

export const routes = (
  <>
                <Route path="/dietas-gastos" element={<Navigate to="/Nominas?tab=dietas" replace />} />

                {/* Legacy PascalCase (pre-fusión Compensación, ADR-0005) → tab Dietas. */}
                <Route path="/DietasGastos" element={<Navigate to="/nominas?tab=dietas" replace />} />


                {/* RRHH & Nóminas */}
                <Route path="/RRHHEmpleados" element={<RedirectKeepSearch to="/rrhh-empleados" />} />

                <Route path="/rrhh-empleados" element={<PermissionGuard permission="hr.view" fallback={<Navigate to="/" replace />}><RRHHEmpleados /></PermissionGuard>} />

                <Route path="/nominas" element={<PermissionGuard permission="payroll.view" fallback={<Navigate to="/" replace />}><Nominas /></PermissionGuard>} />

                {/* Audit descubribilidad — `/nominas/revisor` y `/salary-simulator`
                    se centralizan en la pestaña "Nóminas" tras la fusión del
                    Revisor (PR #694) + Simulador como tab. Redirects mantienen
                    los bookmarks legacy operativos. */}
                <Route path="/nominas/revisor" element={<Navigate to="/nominas" replace />} />

                <Route path="/payrolls/:id" element={<PermissionGuard permission="payroll.view" fallback={<Navigate to="/" replace />}><PayrollDetail /></PermissionGuard>} />

                <Route path="/salary-simulator" element={<Navigate to="/nominas?tab=simulador" replace />} />

                {/* Legacy PascalCase (pre-fusión Compensación, ADR-0005) → tab Simulador. */}
                <Route path="/SalarySimulator" element={<Navigate to="/nominas?tab=simulador" replace />} />

                <Route path="/contratos-rrhh" element={<PermissionGuard permission="hr.view" fallback={<Navigate to="/" replace />}><Navigate to="/Onboarding?tab=contratos" replace /></PermissionGuard>} />

                <Route path="/employee-detail" element={<EmployeeDetail />} />

                <Route path="/portal-empleado" element={<PortalEmpleado />} />

                <Route path="/organigrama" element={<Navigate to="/RRHHEmpleados?tab=organigrama" replace />} />

                <Route path="/onboarding" element={<Onboarding />} />

                <Route path="/skills-management" element={<Navigate to="/rrhh-empleados?tab=skills" replace />} />

                {/* Alias legacy PascalCase — la ruta kebab no matchea por el guion (RR v7 case-insensitive no salva '-') */}
                <Route path="/SkillsManagement" element={<Navigate to="/rrhh-empleados?tab=skills" replace />} />

                <Route path="/hr-communications" element={<Navigate to="/Onboarding?tab=comunicaciones" replace />} />

                {/* PR4-bis #11 — ManualRRHH fused into UserManual hub as the
                    "rrhh" tab. /manual-rrhh keeps working as a redirect. */}
                <Route path="/manual-rrhh" element={<Navigate to="/user-manual?tab=rrhh" replace />} />

                <Route path="/informes-rrhh" element={<PermissionGuard permission="hr.view" fallback={<Navigate to="/" replace />}><InformesRRHH /></PermissionGuard>} />

                <Route path="/timesheet-manager" element={<TimesheetManager />} />

                {/* Lifecycle */}
                <Route path="/ContratosRRHH" element={<Navigate to="/onboarding?tab=contratos" replace />} />

                <Route path="/HRCommunications" element={<Navigate to="/onboarding?tab=comunicaciones" replace />} />

                {/* MyZone — canónica /portal-empleado es hyphenated; añade render PascalCase + legacy */}
                <Route path="/PortalEmpleado" element={<PortalEmpleado />} />

                <Route path="/SupervisorPanel" element={<Navigate to="/PortalEmpleado?tab=supervisor" replace />} />

                {/* Tiempo — canónica /timesheet-manager es hyphenated; añade render PascalCase + legacy */}
                <Route path="/TimesheetManager" element={<TimesheetManager />} />

                <Route path="/Ausencias" element={<Navigate to="/TimesheetManager?tab=ausencias" replace />} />

                <Route path="/AprobacionHoras" element={<Navigate to="/TimesheetManager?tab=aprobaciones" replace />} />

                <Route path="/ControlHorario" element={<Navigate to="/TimesheetManager?tab=control" replace />} />

                <Route path="/supervisor-panel" element={<Navigate to="/PortalEmpleado?tab=supervisor" replace />} />
  </>
);
